import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Send, Loader2, ShieldCheck, Settings2, PenSquare } from "lucide-react";
import { adminGetEmailSettings, adminSaveEmailSettings, adminSendTestEmail } from "@/lib/api";
import AdminEmailCompose from "@/pages/admin/AdminEmailCompose";

const inputCls = "w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none";
const HOST_PRESETS = [
  { label: "Zoho Mail — paid / org (smtppro.zoho.com)", value: "smtppro.zoho.com" },
  { label: "Zoho Mail — India datacenter (smtppro.zoho.in)", value: "smtppro.zoho.in" },
  { label: "Zoho Mail — EU datacenter (smtppro.zoho.eu)", value: "smtppro.zoho.eu" },
  { label: "Zoho Mail — free / personal (smtp.zoho.com)", value: "smtp.zoho.com" },
  { label: "Zoho ZeptoMail (smtp.zeptomail.com)", value: "smtp.zeptomail.com" },
];

function SmtpSettings() {
  const [cfg, setCfg] = useState(null);
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState("");

  const load = () => adminGetEmailSettings().then((d) => { setCfg(d); setTestTo(d.from_email || d.username || ""); });
  useEffect(() => { load(); }, []);

  if (!cfg) return <div className="py-20 text-center text-ink-3"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...cfg };
      if (password) payload.password = password;
      const d = await adminSaveEmailSettings(payload);
      setCfg(d); setPassword("");
      toast.success("Email settings saved.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save settings.");
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const d = await adminSendTestEmail(testTo.trim());
      toast.success(d.message || "Test email sent.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send test email.");
    } finally { setTesting(false); }
  };

  return (
    <div className="max-w-2xl">
      <div className="space-y-5 border border-line bg-surface p-6">
        <label className="flex items-center gap-3 text-sm text-ink-2">
          <input type="checkbox" data-testid="email-enabled" checked={!!cfg.enabled} onChange={(e) => set("enabled", e.target.checked)} className="h-4 w-4" />
          Email sending enabled
        </label>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs uppercase tracking-wide text-ink-3">SMTP host</label>
            <input list="host-presets" data-testid="email-host" value={cfg.host} onChange={(e) => set("host", e.target.value)} className={inputCls} placeholder="smtppro.zoho.com" />
            <datalist id="host-presets">
              {HOST_PRESETS.map((h) => <option key={h.value} value={h.value}>{h.label}</option>)}
            </datalist>
            <p className="mt-1 text-xs text-ink-3">If you get an "Authentication Failed" error, your Zoho account may be in a regional datacenter — try <code>smtppro.zoho.in</code> or <code>.eu</code>.</p>
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wide text-ink-3">Port</label>
            <select data-testid="email-port" value={cfg.port} onChange={(e) => { const p = parseInt(e.target.value); set("port", p); set("starttls", p !== 465); }} className={inputCls}>
              <option value={587}>587 (STARTTLS)</option>
              <option value={465}>465 (SSL)</option>
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm text-ink-2">
              <input type="checkbox" data-testid="email-starttls" checked={!!cfg.starttls} onChange={(e) => set("starttls", e.target.checked)} className="h-4 w-4" />
              Use STARTTLS
            </label>
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wide text-ink-3">SMTP username (mailbox)</label>
            <input data-testid="email-username" value={cfg.username} onChange={(e) => set("username", e.target.value)} className={inputCls} placeholder="no-reply@dharmartha.org" />
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wide text-ink-3">App-specific password</label>
            <input data-testid="email-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder={cfg.password_set ? "•••••••• (leave blank to keep)" : "Zoho app password"} />
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wide text-ink-3">From email</label>
            <input data-testid="email-from" value={cfg.from_email} onChange={(e) => set("from_email", e.target.value)} className={inputCls} placeholder="no-reply@dharmartha.org" />
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wide text-ink-3">From name</label>
            <input data-testid="email-from-name" value={cfg.from_name} onChange={(e) => set("from_name", e.target.value)} className={inputCls} placeholder="Dharmartha" />
          </div>
        </div>

        {cfg.password_set && (
          <p className="flex items-center gap-2 text-xs text-green-700"><ShieldCheck className="h-4 w-4" /> A password is stored. Leave the field blank to keep it.</p>
        )}

        <div className="flex flex-wrap gap-3 border-t border-line pt-4">
          <button onClick={save} disabled={saving} data-testid="email-save" className="inline-flex items-center gap-2 bg-[var(--saffron)] px-5 py-2.5 text-sm text-white transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save settings
          </button>
        </div>
      </div>

      <div className="mt-6 border border-line bg-surface p-6">
        <h2 className="mb-1 font-serif-latin text-lg text-ink">Send a test email</h2>
        <p className="mb-4 text-sm text-ink-3">Verify your Zoho setup. If this succeeds, password-reset and campaign emails will be delivered.</p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className="mb-1 block text-xs uppercase tracking-wide text-ink-3">Recipient</label>
            <input data-testid="email-test-to" value={testTo} onChange={(e) => setTestTo(e.target.value)} className={inputCls} placeholder="you@example.com" />
          </div>
          <button onClick={sendTest} disabled={testing} data-testid="email-test-send" className="inline-flex items-center gap-2 border border-saffron px-5 py-2.5 text-sm text-saffron transition-colors hover:bg-saffron hover:text-white disabled:opacity-60">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send test email
          </button>
        </div>
      </div>
    </div>
  );
}

const TABS = [
  { key: "compose", label: "Compose & Schedule", icon: PenSquare },
  { key: "settings", label: "SMTP Settings", icon: Settings2 },
];

export default function AdminEmail() {
  const [tab, setTab] = useState("compose");
  return (
    <div data-testid="admin-email">
      <div className="mb-5 flex items-center gap-3">
        <Mail className="h-6 w-6 text-saffron" />
        <div>
          <h1 className="font-serif-latin text-2xl text-ink">Email</h1>
          <p className="text-sm text-ink-3">Compose, test, send and schedule announcements &amp; digests — and configure your Zoho SMTP connection.</p>
        </div>
      </div>

      <div className="mb-6 flex gap-1 border-b border-line">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`email-tab-${t.key}`}
            className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm transition-colors ${tab === t.key ? "border-saffron text-saffron" : "border-transparent text-ink-2 hover:text-ink"}`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "compose" ? <AdminEmailCompose /> : <SmtpSettings />}
    </div>
  );
}
